/* eslint-disable camelcase */
import { getFinalGroups, getMarketplaces, getRanksForCustomSplitCandidates } from '../../helpers/pricing';
import { formatFloat } from '../../shared/utils/helpers';
import { isValidValue } from '../../shared/utils/validations';
import { FIELDS_TAKE_SERVER_VALUE } from '../../helpers/constants';

const initialState = {
  groups: [],
  groupsMarketplaceComps: { ticketnetwork: {}, seatgeek: {} },
  initialGroups: [],
  initialMarketPlaces: [{}],
  isPushEnable: false,
  pushEnable: false,
  loadingGroups: true,
  groupFields: {},
  groupsLength: 0,
  groupsBasicInfo: true,
  compsLoading: true,
  marketplaces: [{}],
  broadcastingStrategy: [{}],
};

const getGroupMarketplaceComps = groupsWithComps => {
  return groupsWithComps.reduce(
    (acc, { id, sg_sections, tnd_sections }) => {
      acc.seatgeek[id] = sg_sections;
      acc.ticketnetwork[id] = tnd_sections;
      return acc;
    },
    { seatgeek: {}, ticketnetwork: {} }
  );
};

const resolveCustomSplit = item => {
  if (item.custom_split) return item.custom_split;
  if (item.ranks && item.ranks.length > 0) {
    const candidates = getRanksForCustomSplitCandidates(item.ranks);
    if (candidates.length > 0) {
      const longest = candidates.reduce((best, rank) => {
        const bestLen = Array.isArray(best.custom_split) ? best.custom_split.length : 0;
        const rankLen = Array.isArray(rank.custom_split) ? rank.custom_split.length : 0;
        return rankLen > bestLen ? rank : best;
      });
      return longest.custom_split || 1;
    }
  }
  return 1;
};

const groupsSelector = groups =>
  groups.map(item => ({
    ...item,
    floor: formatFloat(item.floor),
    ceiling: isValidValue(item.ceiling) ? formatFloat(item.ceiling) : '',
    split_type: item.split_type || item.ranks[0]?.split_type || '',
    custom_split: resolveCustomSplit(item),
  }));

const valueToUse = (groups, stateGroups) => groupsSelector(getFinalGroups(groups, stateGroups));

const addComps = (groups, compsArray) => {
  return groups.map(item => ({
    ...item,
    has_dynamic_spaceout: compsArray.filter(compInfo => compInfo.id === item.id)[0]?.has_dynamic_spaceout,
    ranks: compsArray.filter(compInfo => compInfo.id === item.id)[0]?.ranks,
    sg_sections: compsArray.filter(compInfo => compInfo.id === item.id)[0]?.sg_sections,
    tnd_sections: compsArray.filter(compInfo => compInfo.id === item.id)[0]?.tnd_sections,
    marketplace:
      item.marketplace && item.marketplace.length > 0
        ? item.marketplace
        : compsArray.filter(compInfo => compInfo.id === item.id)[0]?.marketplace,
  }));
};

const fieldsToSkip = {
  sg_sections: 'comps',
  tnd_sections: 'comps',
};

export const checkUIFieldInfo = (fieldAsString, UIchanges, group, stateGroups, filterWord = null) => {
  let wordFilter = null;
  if (!filterWord) {
    wordFilter = fieldAsString;
  } else {
    wordFilter = filterWord;
  }
  return Object.keys(UIchanges).includes(group.id.toString()) && [
    UIchanges[group.id].enablePush?.filter(field => field === `${wordFilter}`).length > 0,
  ]
    ? stateGroups.filter(groups => groups.id === group.id)[0][`${fieldAsString}`]
    : group[`${fieldAsString}`];
};

export const groupsRetainUIInfo = (APIgroups, stateGroups, UIchanges) => {
  return APIgroups.map(group => {
    const updatedGroup = { ...group };
    Object.keys(group).forEach(key => {
      if ((fieldsToSkip[key] && group[key].includes(fieldsToSkip[key])) || FIELDS_TAKE_SERVER_VALUE.includes(key)) {
        updatedGroup[key] = group[key];
      } else {
        updatedGroup[key] = checkUIFieldInfo(key, UIchanges, group, stateGroups);
      }
    });
    return updatedGroup;
  });
};

const retainMarketplace = (APIGroups, UIGroups) => {
  if (UIGroups && UIGroups.length > 0) {
    const compResults = APIGroups.map(APIgroup => ({
      ...APIgroup,
      marketplace:
        UIGroups.filter(UIgroup => UIgroup.id === APIgroup.id).length > 0
          ? UIGroups.filter(UIgroup => UIgroup.id === APIgroup.id)[0].marketplace
          : '',
    }));
    return compResults;
  }
  return APIGroups;
};

const checkInfoToRetain = (APIgroups, stateGroups, UIchanges) => {
  if (Object.keys(UIchanges).length) {
    return groupsRetainUIInfo(APIgroups, stateGroups, UIchanges);
  }
  return retainMarketplace(APIgroups, stateGroups);
};

export const setLoadingGroupsCompsAction = status => ({
  type: 'SET_LOADING_GROUPS_COMPS',
  payload: status,
});

export const filterFieldsToRetain = (groupsFieldPayload, groupsFieldState, groupId) => {
  if (Object.keys(groupsFieldState).length > 0) {
    if (groupId) {
      if (Object.keys(groupsFieldState).includes(toString(groupId))) {
        const { groupId: _, ...filteredGroupsFieldState } = groupsFieldState;
        return filteredGroupsFieldState;
      }
      return groupsFieldState;
    }
  }
  return groupsFieldPayload;
};

const checkMarketplaces = (actionPayload, stateMP, changes) => {
  const groupsAmountDiff = actionPayload.length - stateMP.length;
  if (groupsAmountDiff === 1) {
    if (changes && Object.keys(changes).length > 0) {
      const newMP = getMarketplaces(actionPayload);
      const mpChanges = Object.fromEntries(
        Object.entries(changes).filter(([, value]) => value.enablePush.includes('inclusions_exclusions'))
      );
      if (
        mpChanges &&
        Object.keys(mpChanges) &&
        Object.keys(mpChanges).length > 0 &&
        stateMP.groups_ids &&
        stateMP.groups_ids.length > 0
      ) {
        Object.keys(mpChanges).map(
          // eslint-disable-next-line no-return-assign
          groupId =>
            (newMP[newMP.groups_ids.indexOf(Number(groupId))] = stateMP[stateMP.groups_ids.indexOf(Number(groupId))])
        );
        return newMP;
      }
    }
    return getMarketplaces(actionPayload);
  }
  return stateMP;
};

const getBroadcastingStrategy = allGroups => {
  const broadcastingStrategy = allGroups.reduce((acc, item) => {
    acc[item.id] = item?.broadcasting_strategy ?? [];
    return acc;
  }, {});
  return broadcastingStrategy;
};

const checkAdvanceComparisonsChecks = (previousComps, newComps) => {
  if (!previousComps || previousComps.length === 0) {
    return newComps;
  }
  const currentLowRow = previousComps.filter(comp => comp.low_row !== '')?.[0]?.low_row;
  const currentHighRow = previousComps.filter(comp => comp.high_row !== '')?.[0]?.high_row;
  const currentZoneType = previousComps.filter(comp => comp.zone_type !== '')?.[0]?.zone_type;

  if (currentLowRow && currentHighRow) {
    const updatedComps = newComps.map(comp => {
      if (comp.low_row === '' && comp.high_row === '') {
        return {
          ...comp,
          low_row: currentLowRow,
          high_row: currentHighRow,
          zone_type: currentZoneType,
        };
      }
      return comp;
    });
    return updatedComps;
  }
  return newComps;
};

function refreshGroups(state = initialState, action) {
  switch (action.type) {
    case 'SET_MARKETPLACES':
      return { ...state, marketplaces: checkMarketplaces(action.marketplaces, state.marketplaces) };
    case 'REMOVE_MARKETPLACE':
      state.marketplaces.splice(state.marketplaces.groups_ids.indexOf(action.groupId), 1);
      state.marketplaces.groups_ids.splice(state.marketplaces.groups_ids.indexOf(action.groupId), 1);
      return { ...state, marketplaces: state.marketplaces };
    case 'SET_GROUPS':
      return { ...state, groups: checkInfoToRetain(action.payload, state.groups, state.groupFields) };
    case 'SET_GROUPS_BASIC_INFO':
      return {
        ...state,
        groups: valueToUse(action.payload, state.groups),
        groupsLength: action.payload.length,
        groupsBasicInfo: true,
      };
    case 'SET_GROUPS_COMPS':
      // eslint-disable-next-line no-case-declarations
      const groupsWithComps = addComps(state.groups, action.payload);
      return {
        ...state,
        groups: groupsWithComps,
        initialGroups: groupsSelector(groupsWithComps),
        initialMarketPlaces: getMarketplaces(groupsWithComps),
        marketplaces:
          state.marketplaces[0] && Object.keys(state.marketplaces[0]).length === 0
            ? getMarketplaces(groupsWithComps)
            : checkMarketplaces(groupsWithComps, state.marketplaces, state.groupFields),
        groupsBasicInfo: false,
        groupsMarketplaceComps: getGroupMarketplaceComps(groupsWithComps),
        broadcastingStrategy: getBroadcastingStrategy(groupsWithComps),
      };
    case 'SET_INITIAL_GROUPS':
      return { ...state, initialGroups: action.payload };
    case 'SET_INITIAL_MARKEPTLACES':
      return { ...state, initialMarketPlaces: action.payload };
    case 'SET_GROUP_FIELDS':
      return { ...state, groupFields: filterFieldsToRetain(action.fields, state.groupFields, action.group) };
    case 'SET_PUSH_ENABLE':
      if (action.payload !== null) {
        return { ...state, isPushEnable: action.payload };
      }
      return { ...state };
    case 'SET_LOADING_GROUPS':
      return { ...state, loadingGroups: action.payload };
    case 'SET_LOADING_GROUPS_COMPS':
      return { ...state, compsLoading: action.payload };
    case 'UPDATE_GROUPS_MARKETPLACE_COMPS': {
      const marketplaceKey = action.marketplace;
      if (!marketplaceKey) {
        return state;
      }
      return {
        ...state,
        groupsMarketplaceComps: {
          ...state.groupsMarketplaceComps,
          [marketplaceKey]: {
            ...state.groupsMarketplaceComps[marketplaceKey],
            [action.groupId]: checkAdvanceComparisonsChecks(
              state.groupsMarketplaceComps[marketplaceKey][action.groupId],
              action.comps
            ),
          },
        },
      };
    }
    case 'UPDATE_BROADCASTING_STRATEGY':
      return { ...state, broadcastingStrategy: { ...state.broadcastingStrategy, ...action.payload } };

    default:
      return state;
  }
}

export default refreshGroups;
